import React, { useCallback, useContext } from 'react';
import styled, { css } from 'styled-components';
import * as api from '@ubeya/shared/services/api';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as DownloadSvgIcon } from '@ubeya/shared-web/assets/download.svg';
import { FILE_TYPE_ICON_MAP } from '../../helpDeskConstants';
import { isPdfFile, getFileType, formatFileSize } from '../../helpDeskUtils';

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  width: 100%;
  max-width: 350px;
  transition: opacity 0.15s;

  ${({ $variant, theme }) =>
    $variant === 'admin'
      ? css`
          background: ${theme.colors.white};
          color: ${theme.colors.black};
        `
      : css`
          background: ${theme.colors.white};
          color: ${theme.colors.black};
        `}
`;

const FileIconBox = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 36px;
    height: 36px;
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const FileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: ${({ $compact }) => ($compact ? '10px' : '12px')};
  opacity: 0.7;
  line-height: 16px;
`;

const DownloadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: 16px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.grey1};
  }

  svg {
    width: 20px;
    height: 20px;

    path {
      fill: ${({ theme }) => theme.colors.grey4};
    }
  }
`;

const FileAttachmentCard = ({ attachment, variant, accountId, ticketId, onOpenPdf, compact = false }) => {
  const { t } = useContext(ConfigContext);
  const { Icon: AttachmentIcon, labelKey: fileTypeLabelKey } = FILE_TYPE_ICON_MAP[getFileType(attachment)];

  const handleClick = useCallback(async () => {
    try {
      const res = await api.getHelpDeskAttachmentUrl({ accountId, ticketId, attachmentId: attachment.id });
      const url = res?.data?.url || res?.url;
      if (!url) return;
      if (isPdfFile(attachment) && onOpenPdf) {
        onOpenPdf(url);
      } else {
        window.open(url, '_blank');
      }
    } catch {
      // silent
    }
  }, [accountId, ticketId, attachment, onOpenPdf]);

  return (
    <FileCard $variant={variant} $compact={compact}>
      <FileIconBox>
        <AttachmentIcon />
      </FileIconBox>
      <FileInfo>
        <FileName $compact={compact}>{attachment.fileName || attachment.name}</FileName>
        <FileSize $compact={compact}>
          {t(fileTypeLabelKey)}
          {attachment.size !== undefined && ` · ${formatFileSize(attachment.size)}`}
        </FileSize>
      </FileInfo>
      <DownloadIcon $compact={compact} onClick={handleClick}>
        <DownloadSvgIcon />
      </DownloadIcon>
    </FileCard>
  );
};

export default FileAttachmentCard;
